import { useState } from 'react';
import { Alert, Image, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { ROLAGEM_TECLADO, useTecladoAndroid } from '../components/teclado';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from './AuthContext';
import { nuvemConfigurada, supabase, traduzErro } from '../lib/supabase';
import { WEB_URL } from '../lib/config';
import { EMAIL_SUPORTE } from '../lib/termos';
import { Btn, Field, Sheet } from '../components/ui';
import { C } from '../components/theme';
import { cpfValido, mascaraCpf } from '../lib/cpf';
import { IDIOMAS, setIdioma, t, useIdioma } from '../lib/i18n';
import { paisAparelho } from '../lib/moeda';

const COR = '#8B5CF6';
const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim());

export default function LoginScreen() {
  const a = useAuth();
  const ins = useSafeAreaInsets();
  const teclado = useTecladoAndroid();
  const [modo, setModo] = useState<'entrar' | 'criar'>('entrar');
  // Brasil: CPF. Outros países (ou quem não tem CPF): e-mail.
  const [porCpf, setPorCpf] = useState(paisAparelho() === 'BR');
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [senha2, setSenha2] = useState('');
  const [aceite, setAceite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [idiomasAberto, setIdiomasAberto] = useState(false);
  const idAtual = useIdioma();
  const atual = IDIOMAS.find(i => i.id === idAtual) ?? IDIOMAS[0];

  async function enviar() {
    if (porCpf && !cpfValido(cpf)) return Alert.alert(t('CPF inválido'), t('Confira os 11 números.'));
    if (!porCpf && (!emailOk(email) || /^myplanner\.mestre\+/i.test(email.trim()))) return Alert.alert(t('E-mail inválido'), t('Confira o e-mail.'));
    if (modo === 'criar' && !nome.trim()) return Alert.alert(t('Informe seu nome.'));
    if (porCpf && modo === 'criar' && email.trim() && !emailOk(email)) return Alert.alert(t('E-mail inválido'), t('Confira o e-mail ou deixe em branco.'));
    if (senha.length < 8) return Alert.alert(t('A senha precisa ter 8 caracteres ou mais.'));
    if (modo === 'criar') {
      if (senha !== senha2) return Alert.alert(t('As senhas não conferem.'));
      if (!aceite) return Alert.alert(t('Aceite os termos para continuar.'));
    }
    setBusy(true);
    try {
      if (modo === 'entrar') await a.signIn(porCpf ? cpf : email, senha);
      else if ((await a.signUp(nome, porCpf ? cpf : '', email, senha)) === 'confirmar') {
        Alert.alert(t('Cadastro recebido'), t('Seu acesso ainda precisa ser liberado. Tente entrar em alguns instantes.'));
        setModo('entrar');
      }
    } catch (e) { Alert.alert(t('Não foi possível continuar'), traduzErro(e)); }
    finally { setBusy(false); }
  }

  async function esqueci() {
    if (!porCpf && emailOk(email) && supabase) {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo: WEB_URL });
      if (error) return Alert.alert(t('Não foi possível continuar'), traduzErro(error));
      return Alert.alert(t('Esqueci a senha'), t('Se existir uma conta com {email}, enviamos um link para criar uma senha nova. Confira também o spam.', { email: email.trim() }));
    }
    Alert.alert(t('Esqueci a senha'), t('Para contas que entram com CPF, a senha nova é criada pelo nosso suporte, depois de confirmar que a conta é sua. Escreva para {email} informando o seu CPF ou o e-mail cadastrado.', { email: EMAIL_SUPORTE }));
  }

  return (
    // texto 18 B: a própria rolagem abre espaço para o teclado e leva o campo para cima dele (antes: KeyboardAvoidingView)
    <View style={{ flex: 1, backgroundColor: C.bg }}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 24, paddingTop: ins.top + 60, gap: 14, paddingBottom: 24 + teclado }}>
        <View style={{ alignItems: 'flex-end', marginTop: -40 }}>
          <Pressable onPress={() => setIdiomasAberto(true)} hitSlop={8} accessibilityLabel={t('Idioma')}
            style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 16, backgroundColor: C.card, borderWidth: 1, borderColor: C.line }}>
            <Text style={{ color: C.sub, fontSize: 13 }}>🌐 {atual.nome}</Text>
          </Pressable>
        </View>
        <Image source={require('../assets/icon.png')} style={{ width: 84, height: 84, borderRadius: 22 }} />
        <View style={{ alignItems: 'center', gap: 2 }}>
          <Text style={{ color: C.text, fontSize: 34, fontWeight: '800' }}>My Planner Anne</Text>
          <Text style={{ color: COR, fontSize: 15, fontWeight: '600' }}>{t('Sua rotina em um só lugar')}</Text>
        </View>
        <Text style={{ color: C.sub, fontSize: 15 }}>{modo === 'entrar' ? (porCpf ? t('Entre com seu CPF e sua senha.') : t('Entre com seu e-mail e sua senha.')) : t('Crie sua conta. Seus dados ficam salvos na nuvem e neste dispositivo.')}</Text>
        {!nuvemConfigurada && <Text style={{ color: C.warn }}>{t('Servidor não configurado: faltam os dados do Supabase em lib/config.ts.')}</Text>}

        <View style={{ flexDirection: 'row', backgroundColor: C.card, borderRadius: 14, padding: 4 }}>
          {(['entrar', 'criar'] as const).map(m => (
            <Pressable key={m} onPress={() => setModo(m)} style={{ flex: 1, padding: 10, borderRadius: 10, backgroundColor: modo === m ? COR : 'transparent' }}>
              <Text style={{ color: modo === m ? '#fff' : C.sub, textAlign: 'center', fontWeight: '700' }}>{m === 'entrar' ? t('Entrar') : t('Criar conta')}</Text>
            </Pressable>
          ))}
        </View>

        {modo === 'criar' && <Field value={nome} onChangeText={setNome} placeholder={t('Seu nome')} autoCapitalize="words" />}
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {([[true, t('CPF (Brasil)')], [false, t('E-mail')]] as const).map(([v, l]) => (
            <Pressable key={String(v)} onPress={() => setPorCpf(v)} style={{ paddingVertical: 6, paddingHorizontal: 12, borderRadius: 14, borderWidth: 1,
              borderColor: porCpf === v ? COR : C.line, backgroundColor: porCpf === v ? COR : 'transparent' }}>
              <Text style={{ color: porCpf === v ? '#fff' : C.sub, fontSize: 13, fontWeight: '600' }}>{l}</Text>
            </Pressable>
          ))}
        </View>
        {porCpf
          ? <Field value={cpf} onChangeText={x => setCpf(mascaraCpf(x))} placeholder={t('CPF')} keyboardType="number-pad" maxLength={14} />
          : <Field value={email} onChangeText={setEmail} placeholder={t('E-mail')} keyboardType="email-address" autoCapitalize="none" autoCorrect={false} autoComplete="email" />}
        {modo === 'criar' && porCpf && <Field value={email} onChangeText={setEmail} placeholder={t('E-mail para contato (opcional)')} keyboardType="email-address" autoCapitalize="none" autoCorrect={false} autoComplete="email" />}
        <Field value={senha} onChangeText={setSenha} placeholder={t('Senha (8 caracteres ou mais)')} secureTextEntry autoCapitalize="none" />
        {modo === 'criar' && <Field value={senha2} onChangeText={setSenha2} placeholder={t('Repita a senha')} secureTextEntry autoCapitalize="none" />}
        {modo === 'criar' && (
          <Pressable onPress={() => setAceite(!aceite)} style={{ flexDirection: 'row', gap: 10 }}>
            <Text style={{ fontSize: 20, color: C.text }}>{aceite ? '☑' : '☐'}</Text>
            <Text style={{ flex: 1, color: C.sub, fontSize: 13 }}>
              {t('Concordo que meu nome, documento e e-mail sejam usados apenas para identificar minha conta, e que minhas tarefas e anotações fiquem guardadas nela. Posso apagar a conta e todos os dados a qualquer momento em Ajustes › Segurança.')}
            </Text>
          </Pressable>
        )}
        <Btn cor={COR} label={busy ? t('Aguarde...') : modo === 'entrar' ? t('Entrar') : t('Criar conta')} onPress={enviar} disabled={busy} />
        {modo === 'entrar' && <Pressable onPress={esqueci}><Text style={{ color: C.sub, textAlign: 'center' }}>{t('Esqueci a senha')}</Text></Pressable>}
      </ScrollView>

      <Sheet visible={idiomasAberto} onClose={() => setIdiomasAberto(false)} title={t('Idioma')}>
        <ScrollView contentContainerStyle={{ padding: 16, gap: 8, paddingBottom: ins.bottom + 24 }}>
          {IDIOMAS.map(i => {
            const on = i.id === idAtual;
            return (
              <Pressable key={i.id} onPress={() => { setIdioma(i.id); setIdiomasAberto(false); }}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14,
                  backgroundColor: on ? COR : C.card, borderWidth: 1, borderColor: on ? COR : C.line }}>
                <Text style={{ fontSize: 22 }}>{i.bandeira}</Text>
                <Text style={{ flex: 1, color: on ? '#fff' : C.text, fontSize: 16, fontWeight: on ? '700' : '400' }}>{i.nome}</Text>
                {on && <Text style={{ color: '#fff', fontWeight: '800' }}>✓</Text>}
              </Pressable>
            );
          })}
        </ScrollView>
      </Sheet>
    </View>
  );
}
